import * as Sentry from "@sentry/react";
import { HANDLED_TAG } from "./sentryConfig";

/**
 * Instrumentation for *handled* (non-crash) errors in the chat.
 *
 * Today the only thing Sentry sees is unhandled exceptions and promise
 * rejections (crashes). The ~100 catch blocks across the app either swallow
 * the error (`catch {}`) or surface a toast — the dev team gets zero signal on
 * how often these happen. This helper funnels those handled errors into Sentry
 * as `level: "warning"` events, tagged so they can be counted by feature and
 * kept out of the crash dashboards.
 *
 * QUOTA SAFETY — two independent quotas are protected:
 *   1. Session Replay: every handled event is tagged `handled: "true"`, and
 *      App.tsx's replayIntegration.beforeErrorSampling (shouldRecordReplayForEvent)
 *      drops replay recording for those events. Without that hook,
 *      replaysOnErrorSampleRate: 1.0 would pull a full replay for EVERY handled
 *      warning and blow the replay quota — level alone is NOT enough, because
 *      Sentry's replay error-mode triggers on any error-TYPE event (warnings
 *      included).
 *   2. Error events: a per-key cooldown plus a per-session hard cap (below) stop
 *      a runaway loop (e.g. an SSE that reconnects-and-fails every 100ms) from
 *      sending thousands of identical events.
 */

export type ErrorCategory =
  | "api"
  | "sse"
  | "auth"
  | "payment"
  | "upload"
  | "transcription"
  | "parse"
  | "react";

export interface ReportHandledErrorOptions {
  /** Coarse bucket for dashboard filtering. */
  category: ErrorCategory;
  /** Dotted feature path, e.g. "chat.send" or "chat.ocr.load". Drives grouping. */
  feature: string;
  /** Defaults to "warning" so handled errors stay out of crash dashboards. */
  level?: "warning" | "error";
  /** Structured metadata ONLY — never message text or other PII. */
  context?: Record<string, unknown>;
  /** Extra searchable tags (e.g. { status: "503" }). */
  tags?: Record<string, string>;
}

// The same feature+category is reported at most once per COOLDOWN_MS. Repeats
// inside the window are counted locally and dropped; the count rides along on
// the next event that escapes the window (suppressed_since_last).
const COOLDOWN_MS = 10_000;
// Absolute ceiling per page-load, across all keys — a backstop against a
// pathological loop that cycles through many distinct keys.
const MAX_EVENTS_PER_SESSION = 100;

interface KeyState {
  lastSent: number;
  suppressed: number;
}
const keyStates = new Map<string, KeyState>();
let sessionEventCount = 0;

/** Test-only: reset module-level throttle state between cases. */
export function __resetReportErrorThrottleForTests(): void {
  keyStates.clear();
  sessionEventCount = 0;
}

/**
 * Decide whether an event for `key` may be sent at time `now`, applying the
 * cooldown window and the per-session cap. Stateful (mutates the throttle maps)
 * but deterministic for an injected `now`, so it is unit-testable without
 * Sentry or real timers. When it returns allowed=true it also reports how many
 * events were suppressed for that key since the last send.
 */
export function evaluateThrottle(
  key: string,
  now: number
): { allowed: boolean; suppressed: number } {
  if (sessionEventCount >= MAX_EVENTS_PER_SESSION) {
    return { allowed: false, suppressed: 0 };
  }
  const state = keyStates.get(key);
  if (state && now - state.lastSent < COOLDOWN_MS) {
    state.suppressed += 1;
    return { allowed: false, suppressed: 0 };
  }
  const suppressed = state ? state.suppressed : 0;
  keyStates.set(key, { lastSent: now, suppressed: 0 });
  sessionEventCount += 1;
  return { allowed: true, suppressed };
}

function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Report a handled (non-crash) error to Sentry. No-op (silently throttled) when
 * the same feature+category fired within the cooldown window or the session cap
 * is hit. Safe to call when Sentry is not initialised (dev) — the SDK no-ops.
 */
export function reportHandledError(
  error: unknown,
  opts: ReportHandledErrorOptions
): void {
  const key = `${opts.feature}::${opts.category}`;
  const { allowed, suppressed } = evaluateThrottle(key, Date.now());
  if (!allowed) return;

  const err = error instanceof Error ? error : new Error(safeStringify(error));

  Sentry.captureException(err, {
    level: opts.level ?? "warning",
    tags: {
      [HANDLED_TAG]: "true",
      category: opts.category,
      feature: opts.feature,
      ...(opts.tags ?? {}),
    },
    contexts: {
      handled_error: {
        ...(opts.context ?? {}),
        ...(suppressed > 0 ? { suppressed_since_last: suppressed } : {}),
      },
    },
    // Group by feature+category (NOT by stack/message) so frequency for a given
    // feature lands in one issue instead of fragmenting across stack traces.
    fingerprint: ["handled", opts.feature, opts.category],
  });
}
